import React, { useEffect, useRef, useState } from 'react';
import { colors } from '../../theme';

const ROW_HEIGHT = 32;
const CELL_WIDTH = 42;
const HEADER_HEIGHT = 36;
const BUFFER_ROWS = 4;

const ratioToColor = (ratio) => {
  if (ratio == null) return '#FFFFFF';
  if (Number.isNaN(ratio)) return '#FFFFFF';
  const capped = Math.max(0, Math.min(2, ratio));
  const blend = (a, b, t) => a + (b - a) * t;
  const toRgb = (hex) => {
    const value = hex.replace('#', '');
    return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
  };
  const toHex = (rgb) => `#${rgb.map(v => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
  if (capped <= 1) {
    const start = toRgb(colors.danger || '#EC2124');
    const end = toRgb(colors.success || '#46BB89');
    const rgb = start.map((component, index) => blend(component, end[index], capped));
    return toHex(rgb);
  }
  const start = toRgb(colors.success || '#46BB89');
  const end = toRgb('#2A4B8D');
  const rgb = start.map((component, index) => blend(component, end[index], capped - 1));
  return toHex(rgb);
};

const ExportButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '0.4rem 0.75rem',
      marginLeft: '0.5rem',
      borderRadius: '4px',
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      cursor: 'pointer',
      fontSize: '0.85rem'
    }}
  >
    {children}
  </button>
);

const Tooltip = ({ tooltip }) => {
  if (!tooltip?.visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        left: tooltip.x + 12,
        top: tooltip.y + 12,
        backgroundColor: 'rgba(35,35,35,0.95)',
        color: '#FFFFFF',
        padding: '0.4rem 0.6rem',
        borderRadius: '4px',
        fontSize: '0.75rem',
        pointerEvents: 'none',
        zIndex: 999
      }}
    >
      <div style={{ fontWeight: 600 }}>{tooltip.productNumber}</div>
      <div>{tooltip.monthLabel}</div>
      <div style={{ marginTop: '0.25rem' }}>Qty: {tooltip.qtyProduced}</div>
      <div>Baseline: {tooltip.baseline == null ? 'n/a' : tooltip.baseline}</div>
      <div>Ratio: {tooltip.ratio == null ? 'n/a' : tooltip.ratio}</div>
    </div>
  );
};

const buildCsv = (data) => {
  const lines = ['Product Number,Month,Quantity,Baseline,Ratio'];
  data.rows.forEach(row => {
    row.cells.forEach((cell, index) => {
      const month = data.months[index];
      const fields = [
        row.productNumber,
        month?.label ?? month?.key ?? '',
        cell.qtyProduced,
        cell.baseline == null ? '' : cell.baseline,
        cell.ratio == null ? '' : cell.ratio
      ];
      lines.push(fields.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));
    });
  });
  return lines.join('\n');
};

const downloadBlob = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

const exportHeatmapPng = (data, title) => {
  const width = Math.max(1, data.months.length) * CELL_WIDTH + 120;
  const height = Math.max(1, data.rows.length) * ROW_HEIGHT + HEADER_HEIGHT + 60;
  const canvas = document.createElement('canvas');
  const scale = window.devicePixelRatio || 1;
  canvas.width = width * scale;
  canvas.height = height * scale;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#111111';
  ctx.font = '16px Arial';
  ctx.fillText(title, 16, 22);

  ctx.font = '12px Arial';
  ctx.fillStyle = '#444444';
  ctx.fillText('Ratio colour scale: red (0) ? green (1) ? blue (2)', 16, 40);

  const startX = 110;
  const startY = HEADER_HEIGHT + 20;

  ctx.font = '11px Arial';
  ctx.fillStyle = '#000000';
  data.months.forEach((month, index) => {
    const x = startX + index * CELL_WIDTH;
    ctx.save();
    ctx.translate(x + CELL_WIDTH / 2, HEADER_HEIGHT + 5);
    ctx.rotate(-Math.PI / 3);
    ctx.textAlign = 'left';
    ctx.fillText(month.label || month.key, 0, 0);
    ctx.restore();
  });

  data.rows.forEach((row, rowIndex) => {
    const y = startY + rowIndex * ROW_HEIGHT;
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'right';
    ctx.fillText(row.productNumber, startX - 8, y + ROW_HEIGHT * 0.65);
    row.cells.forEach((cell, cellIndex) => {
      const x = startX + cellIndex * CELL_WIDTH;
      ctx.fillStyle = ratioToColor(cell.ratio);
      ctx.fillRect(x, y, CELL_WIDTH - 2, ROW_HEIGHT - 2);
    });
  });

  const link = document.createElement('a');
  link.href = canvas.toDataURL('image/png');
  link.download = `${title.replace(/\s+/g, '_').toLowerCase()}_heatmap.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ProductionHeatmap = ({
  data,
  loading,
  error,
  selectedProducts,
  onSelectProduct
}) => {
  const bodyRef = useRef(null);
  const headerRef = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(480);
  const [scrollTop, setScrollTop] = useState(0);
  const [tooltip, setTooltip] = useState({ visible: false });

  useEffect(() => {
    if (!bodyRef.current) return;
    const element = bodyRef.current;
    const updateHeight = () => {
      setViewportHeight(element.clientHeight || 480);
    };
    updateHeight();
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateHeight)
      : null;
    if (observer) observer.observe(element);
    const handleResize = () => updateHeight();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (observer) observer.disconnect();
    };
  }, []);

  const totalRows = data?.rows?.length || 0;
  const visibleRowCount = Math.min(
    totalRows,
    Math.ceil(viewportHeight / ROW_HEIGHT) + BUFFER_ROWS
  );
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS);
  const endIndex = Math.min(totalRows, startIndex + visibleRowCount);
  const visibleRows = data?.rows?.slice(startIndex, endIndex) || [];

  const handleScroll = (event) => {
    const { scrollTop: newScrollTop, scrollLeft } = event.currentTarget;
    setScrollTop(newScrollTop);
    if (headerRef.current) {
      headerRef.current.scrollLeft = scrollLeft;
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false });
  };

  const renderTooltip = (event, row, cell) => {
    setTooltip({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      productNumber: row.productNumber,
      qtyProduced: cell.qtyProduced,
      baseline: cell.baseline,
      ratio: cell.ratio,
      monthLabel: cell.label || ''
    });
  };

  const exportCsv = () => {
    if (!data) return;
    const csv = buildCsv(data);
    downloadBlob(csv, 'production_heatmap.csv', 'text/csv;charset=utf-8;');
  };

  const exportPng = () => {
    if (!data) return;
    exportHeatmapPng(data, 'Production Heatmap');
  };

  if (loading) {
    return <div style={{ padding: '1rem' }}>Loading heatmap�</div>;
  }

  if (error) {
    return <div style={{ padding: '1rem', color: colors.danger }}>Failed to load heatmap data.</div>;
  }

  if (!data || !data.rows?.length) {
    return (
      <div style={{ padding: '1rem' }}>
        <div>No data for the selected range.</div>
        <div style={{ fontSize: '0.85rem', color: colors.muted }}>Adjust filters or the date range and try again.</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div style={{ fontWeight: 600 }}>
          Heatmap (baseline {data.baselineWindow}-month average)
          {data?.meta?.dataSource ? (
            <div style={{ fontSize: '0.75rem', color: colors.muted, fontWeight: 400 }}>
              Source: {data.meta.dataSource === 'fallback' ? 'Live aggregation' : 'Cached totals'}
            </div>
          ) : null}
        </div>
        <div>
          <ExportButton onClick={exportCsv}>Export CSV</ExportButton>
          <ExportButton onClick={exportPng}>Export PNG</ExportButton>
        </div>
      </div>
      <div style={{ overflow: 'hidden', border: `1px solid ${colors.border}`, borderRadius: '6px' }}>
        <div
          ref={headerRef}
          style={{
            overflow: 'hidden',
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: '#F4F4F4'
          }}
        >
          <div style={{ display: 'flex' }}>
            <div style={{
              width: 110,
              minWidth: 110,
              padding: '0.5rem',
              fontWeight: 600,
              backgroundColor: '#FFFFFF'
            }}>
              Product
            </div>
            <div style={{ display: 'flex' }}>
              {data.months.map(month => (
                <div
                  key={month.key}
                  style={{
                    width: CELL_WIDTH,
                    minWidth: CELL_WIDTH,
                    padding: '0.35rem 0.2rem',
                    fontSize: '0.7rem',
                    textAlign: 'center',
                    color: '#444444',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {month.label}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div
          ref={bodyRef}
          onScroll={handleScroll}
          style={{
            overflow: 'auto',
            maxHeight: '60vh',
            position: 'relative'
          }}
          onMouseLeave={handleMouseLeave}
        >
          <div style={{ height: totalRows * ROW_HEIGHT, position: 'relative' }}>
            {visibleRows.map((row, rowOffset) => {
              const rowIndex = startIndex + rowOffset;
              const isSelected = selectedProducts?.includes(row.productNumber);
              return (
                <div
                  key={row.productNumber}
                  style={{
                    position: 'absolute',
                    top: rowIndex * ROW_HEIGHT,
                    left: 0,
                    height: ROW_HEIGHT,
                    display: 'flex',
                    width: '100%'
                  }}
                >
                  <button
                    type="button"
                    onClick={(event) => onSelectProduct(row.productNumber, { multi: event.metaKey || event.ctrlKey })}
                    style={{
                      width: 110,
                      minWidth: 110,
                      textAlign: 'left',
                      padding: '0.4rem 0.6rem',
                      border: 'none',
                      borderRight: `1px solid ${colors.border}`,
                      backgroundColor: isSelected ? 'rgba(70,187,137,0.12)' : '#FFFFFF',
                      color: colors.text,
                      cursor: 'pointer'
                    }}
                  >
                    {row.productNumber}
                  </button>
                  <div style={{ display: 'flex' }}>
                    {row.cells.map((cell, cellIndex) => (
                      <div
                        key={`${row.productNumber}-${data.months[cellIndex]?.key || cellIndex}`}
                        onMouseMove={(event) => renderTooltip(event, row, { ...cell, label: data.months[cellIndex]?.label || data.months[cellIndex]?.key })}
                        onClick={(event) => onSelectProduct(row.productNumber, { multi: event.metaKey || event.ctrlKey })}
                        style={{
                          width: CELL_WIDTH,
                          minWidth: CELL_WIDTH,
                          height: ROW_HEIGHT,
                          backgroundColor: ratioToColor(cell.ratio),
                          borderRight: '1px solid #FFFFFF',
                          borderBottom: '1px solid #FFFFFF',
                          cursor: 'pointer',
                          opacity: isSelected || !selectedProducts?.length ? 1 : 0.35,
                          transition: 'opacity 0.2s ease'
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Tooltip tooltip={tooltip} />
    </div>
  );
};

export default ProductionHeatmap;

